/**
 * ─────────────────────────────────────────────
 * src/store/slices/dashboardSlice.js
 * ─────────────────────────────────────────────
 *
 * Manages all dashboard state:
 *   - stats (stat cards)
 *   - projects (overview table + "View All" page)
 *   - milestones (upcoming milestones sidebar)
 *   - graphs (resource allocation, weekly progress, completion donut)
 *
 * WHY separate from projectSlice?
 * Dashboard data is shaped differently from raw project data.
 * /api/dashboard returns computed trackingStatus, progress numbers,
 * milestone tasks — dashboard-specific shapes. Raw project CRUD
 * (create, members, lists) lives in projectSlice.
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dashboardService from "../../services/dashboardService";

const extractError = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong.";

// /api/projects returns the raw stored `status` field (which can hold legacy
// workflow values such as "planning" or "active") rather than a computed
// trackingStatus. Mirror the backend's precedence from dashboard.routes.js
// getTrackingStatus so the table, status dots and card filters agree with the
// stat cards: completed → on-hold → at-risk → on-track.
//
// One backend signal is unavailable here: `hasOverdueTask` needs per-task
// deadlines, which this payload does not include. A project that is at-risk
// only because of an overdue task therefore still reads as on-track locally.
// That is why the stat cards read from /api/dashboard exclusively instead of
// counting this array — see Dashboard.js statCardValue.
const normalizeTrackingStatus = (project) => {
  const status = String(
    project.trackingStatus || project.status || "",
  ).toLowerCase();

  const deadlinePassed = project.deadline
    ? new Date(project.deadline).getTime() < Date.now()
    : false;

  let trackingStatus;
  if (status === "completed" || project.progress === 100) {
    trackingStatus = "completed";
  } else if (status === "on-hold") {
    trackingStatus = "on-hold";
  } else if (status === "at-risk" || deadlinePassed) {
    trackingStatus = "at-risk";
  } else {
    trackingStatus = "on-track";
  }

  return { ...project, trackingStatus };
};

// ─────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────

// Fetches: stats + projectOverview (first 4) + upcomingMilestones
// Used by: Dashboard.js on mount, and when sort changes
export const fetchDashboard = createAsyncThunk(
  "dashboard/fetchDashboard",
  async (sortBy = "", { rejectWithValue }) => {
    try {
      const data = await dashboardService.getDashboard(sortBy);
      return data.data; // { stats, projectOverview, upcomingMilestones }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Fetches full project list (used by Layout.js, Projects.js, and sort)
export const fetchDashboardProjects = createAsyncThunk(
  "dashboard/fetchDashboardProjects",
  async (sortBy = "", { rejectWithValue }) => {
    try {
      const data = await dashboardService.getDashboardProjects(sortBy);
      return data.data; // array of projects
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Fetches the separate admin-only disabled-project collection.
export const fetchDisabledProjects = createAsyncThunk(
  "dashboard/fetchDisabledProjects",
  async (sortBy = "", { rejectWithValue }) => {
    try {
      const data = await dashboardService.getDashboardProjects(sortBy, true);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Fetches graph data (resource allocation, weekly progress, donut)
export const fetchDashboardGraphs = createAsyncThunk(
  "dashboard/fetchDashboardGraphs",
  async (projectId = "", { rejectWithValue }) => {
    try {
      const data = await dashboardService.getDashboardGraphs(projectId);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ─────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────
const initialState = {
  // Stat cards (Total, On Track, At Risk, Completed)
  stats: null,

  // Project overview table (first 4 from /api/dashboard)
  projectOverview: [],

  // Full project list (from /api/dashboard/projects)
  // Used by Layout.js (sidebar count), Projects.js (project list)
  projects: [],
  disabledProjects: [],

  // Upcoming milestones sidebar
  upcomingMilestones: [],

  // Graph data
  weeklyProgress: [],
  overallCompletion: null,
  resourceAllocation: [],

  // Loading flags — separate per feature
  isDashboardLoading: false,
  isProjectsLoading: false,
  isDisabledProjectsLoading: false,
  isGraphsLoading: false,

  // Errors
  dashboardError: null,
  projectsError: null,
  disabledProjectsError: null,
  graphsError: null,
};

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,

  reducers: {
    // When Layout.js creates a project, append it to projects list locally
    // so the UI updates immediately without re-fetching
    addProjectToList: (state, action) => {
      state.projects = [action.payload, ...state.projects];
    },

    // Clear all dashboard errors
    clearDashboardErrors: (state) => {
      state.dashboardError = null;
      state.projectsError = null;
      state.graphsError = null;
    },
  },

  extraReducers: (builder) => {
    // ── fetchDashboard ────────────────────────
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.isDashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.isDashboardLoading = false;
        state.stats = action.payload.stats;
        state.projectOverview = action.payload.projectOverview;
        state.upcomingMilestones = action.payload.upcomingMilestones;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.isDashboardLoading = false;
        state.dashboardError = action.payload;
      });

    // ── fetchDashboardProjects ────────────────
    builder
      .addCase(fetchDashboardProjects.pending, (state) => {
        state.isProjectsLoading = true;
        state.projectsError = null;
      })
      .addCase(fetchDashboardProjects.fulfilled, (state, action) => {
        state.isProjectsLoading = false;
        state.projects = action.payload.map(normalizeTrackingStatus);
      })
      .addCase(fetchDashboardProjects.rejected, (state, action) => {
        state.isProjectsLoading = false;
        state.projectsError = action.payload;
      });

    builder
      .addCase(fetchDisabledProjects.pending, (state) => {
        state.isDisabledProjectsLoading = true;
        state.disabledProjectsError = null;
      })
      .addCase(fetchDisabledProjects.fulfilled, (state, action) => {
        state.isDisabledProjectsLoading = false;
        state.disabledProjects = action.payload.map(normalizeTrackingStatus);
      })
      .addCase(fetchDisabledProjects.rejected, (state, action) => {
        state.isDisabledProjectsLoading = false;
        state.disabledProjectsError = action.payload;
      });

    // ── fetchDashboardGraphs ──────────────────
    builder
      .addCase(fetchDashboardGraphs.pending, (state) => {
        state.isGraphsLoading = true;
        state.graphsError = null;
      })
      .addCase(fetchDashboardGraphs.fulfilled, (state, action) => {
        state.isGraphsLoading = false;
        state.weeklyProgress = action.payload.weeklyProgress;
        state.overallCompletion = action.payload.overallCompletion;
        state.resourceAllocation = action.payload.resourceAllocation;
      })
      .addCase(fetchDashboardGraphs.rejected, (state, action) => {
        state.isGraphsLoading = false;
        state.graphsError = action.payload;
      });
  },
});

export const { addProjectToList, clearDashboardErrors } =
  dashboardSlice.actions;

export default dashboardSlice.reducer;
